import clsx from 'clsx';
import ClientCombobox from 'components/comboboxes/ClientCombobox';
import RoleIdCombobox from 'components/comboboxes/RoleIdCombobox';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import HubertPopover from 'components/HubertUI/HubertPopover';
import { differenceInMinutes, format } from 'date-fns';
import { useTopBar } from 'hooks/useTopBar';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import InterviewState from '../InterviewState';
import { useScoreInterview } from './scoring.context';

function truncateString(s: string, cutoff: number) {
  if (s.length > cutoff) {
    return `${s.substring(0, cutoff)}...`;
  }
  return s;
}

const ScoreInterviewHeader = () => {
  const { interview, template, job, candidate } = useScoreInterview();
  const [pageTitle, setPageTitle] = useState<ReactNode>();
  const [interviewNode, setInterviewNode] = useState<ReactNode>();
  const [jobNode, setJobNode] = useState<ReactNode>();
  const [templateNode, setTemplateNode] = useState<ReactNode>();
  const [candidateNode, setCandidateNode] = useState<ReactNode>();

  useEffect(() => {
    if (interview) {
      const scoringBuild = interview.isNewBuild === true ? 'New build' : 'Old build';
      setInterviewNode(
        <div className="hubert-vertical-center flex flex-row justify-between gap-2">
          <HubertBadge variant={interview.processId ? 'info' : 'danger'}>
            {interview.processId ?? 'Process ID missing'}
          </HubertBadge>

          <HubertBadge variant={'info'}>{scoringBuild}</HubertBadge>

          <HubertPopover>
            <HubertPopover.Trigger>
              <InterviewState interview={interview} className="cursor-pointer" />
            </HubertPopover.Trigger>

            <HubertPopover.Content
              className={clsx('w-[500px] min-w-[500px]', 'flex flex-col justify-between gap-4', 'p-4')}
            >
              {interview.applicationId ? (
                <div className={clsx('w-full', 'flex flex-row justify-between')}>
                  <div>Application ID:</div>
                  <div>{interview.applicationId}</div>
                </div>
              ) : null}

              {interview.interviewLoaded ? (
                <div className={clsx('w-full', 'flex flex-row justify-between')}>
                  <div>Loaded:</div>
                  <div>{format(new Date(interview.interviewLoaded), 'PPpp')}</div>
                </div>
              ) : null}

              {interview.interviewStarted ? (
                <div className={clsx('w-full', 'flex flex-row justify-between')}>
                  <div>Started:</div>
                  <div>{format(new Date(interview.interviewStarted), 'PPpp')}</div>
                </div>
              ) : null}

              {interview.interviewEnded ? (
                <div className={clsx('w-full', 'flex flex-row justify-between')}>
                  <div>Ended:</div>
                  <div>{format(new Date(interview.interviewEnded), 'PPpp')}</div>
                </div>
              ) : null}

              {interview.interviewEnded && interview.interviewLoaded ? (
                <div className={clsx('w-full', 'flex flex-row justify-between')}>
                  <div>Length:</div>
                  <div>
                    {differenceInMinutes(new Date(interview.interviewEnded), new Date(interview.interviewLoaded))}{' '}
                    minutes
                  </div>
                </div>
              ) : null}
            </HubertPopover.Content>
          </HubertPopover>
        </div>,
      );
    }
  }, [interview]);

  useEffect(() => {
    if (job) {
      setJobNode(
        <div className="flex flex-row justify-between gap-2">
          <HubertPopover>
            <HubertPopover.Trigger>
              <HubertBadge variant={'outline'} className={clsx('cursor-pointer truncate')}>
                {job.title ? truncateString(job.title, 40) : 'No title set for this job'}
              </HubertBadge>
            </HubertPopover.Trigger>
            <HubertPopover.Content
              className={clsx('w-[500px] min-w-[500px]', 'flex flex-col justify-between gap-4', 'p-4')}
            >
              <div>{job.title}</div>

              <ClientCombobox value={job.client_id} disabled={true} />
              <RoleIdCombobox value={job.role_id} disabled={true} />

              <div className="flex flex-row justify-end">
                <Link to={`/jobs/${job._id}`} className="cursor-pointer">
                  <HubertBadge>Go to job</HubertBadge>
                </Link>
              </div>
            </HubertPopover.Content>
          </HubertPopover>

          <div className="flex flex-col justify-end">
            <ClientCombobox value={job.client_id} disabled={true} />
            <RoleIdCombobox value={job.role_id} disabled={true} />
          </div>
          {/* <HubertBadge variant={'info'}>{job.role_id ?? 'No role ID set'}</HubertBadge> */}
        </div>,
      );
    }
  }, [job]);

  useEffect(() => {
    if (template) {
      setTemplateNode(
        <div className="flex flex-row justify-between gap-2">
          <HubertPopover>
            <HubertPopover.Trigger>
              <HubertBadge variant={'outline'}>Template</HubertBadge>
            </HubertPopover.Trigger>
            <HubertPopover.Content
              className={clsx('w-[500px] min-w-[500px]', 'flex flex-col justify-between gap-4', 'p-4')}
            >
              <div>{template.name}</div>

              <HubertBadge variant={'info'}>Process ID: {template.process_id}</HubertBadge>

              <div className="flex flex-row justify-end">
                <Link to={`/templates/${template._id}/${template.process_id}`} className="cursor-pointer">
                  <HubertBadge>Go to template</HubertBadge>
                </Link>
              </div>
            </HubertPopover.Content>
          </HubertPopover>
        </div>,
      );
    }
  }, [template]);

  useEffect(() => {
    if (candidate) {
      let name = 'Candidate';
      if (candidate.first_name && candidate.first_name.length) {
        name = `${candidate.first_name} ${candidate.last_name}`;
      }

      setCandidateNode(
        <div className="flex flex-row justify-between gap-2">
          <HubertPopover>
            <HubertPopover.Trigger>
              <HubertBadge variant={'outline'}>Candidate</HubertBadge>
            </HubertPopover.Trigger>
            <HubertPopover.Content
              className={clsx('w-[500px] min-w-[500px]', 'flex flex-col justify-between gap-4', 'p-4')}
            >
              <div>{name}</div>

              <div className="flex flex-row justify-end">
                <Link to={`/candidates/${candidate._id}`} className="cursor-pointer">
                  <HubertBadge>Go to candidate</HubertBadge>
                </Link>
              </div>
            </HubertPopover.Content>
          </HubertPopover>
        </div>,
      );
    }
  }, [candidate]);

  useEffect(() => {
    if (interview && template && job) {
      setPageTitle(
        <div className={clsx('w-full', 'hubert-vertical-center flex flex-row justify-between gap-4')}>
          <div>Score interview</div>

          <div className={clsx('flex flex-row justify-start gap-4')}>
            {interviewNode}
            {jobNode}
            {templateNode}
            {candidateNode}
          </div>
        </div>,
      );
    }
  }, [templateNode, jobNode, interviewNode, candidateNode]);

  useTopBar({
    stringTitle: `Score - ${job?.title}`,
    nodeTitle: pageTitle,
  });

  return null;
};

export default ScoreInterviewHeader;
